import type { RevenueByCurrencyDto, VolumeByCurrencyDto } from "@/features/daily/types/daily.types";
import { dailyFormat } from "@/features/daily/components/command/command-view-model";

/**
 * D136: every volume and revenue figure says what it is made of. Volume = deposits +
 * orders not paid with balance; the balance-paid orders are named apart because the
 * deposit that funded them is already counted. Revenue counts every operation.
 * D139: a breakdown only when BOTH types have something — otherwise the total says it all.
 */
const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === "object" && value !== null && !Array.isArray(value);

const isShare = (value: unknown): boolean => isRecord(value) && typeof value.amount === "number" && typeof value.sample === "number";

const hasShares = (value: unknown): boolean => isRecord(value) && isShare(value.order) && isShare(value.deposit);

const hasVolumeSplit = (row: VolumeByCurrencyDto): boolean => {
    const value: unknown = row;
    return isRecord(value) && hasShares(value.byType) && isShare(value.balanceFundedOrders);
};

export const moneyByType = {
    /** D139: split a figure by type only when both parts are non-zero. */
    bothPresent: (order: number, deposit: number): boolean => order !== 0 && deposit !== 0,

    volumeLines: (row: VolumeByCurrencyDto | undefined): string[] => {
        // Guardian P3: a payload from before B18 has no split; say nothing rather than crash.
        if (row === undefined || !hasVolumeSplit(row)) return [];
        const { currency, byType, balanceFundedOrders } = row;
        return [
            ...(moneyByType.bothPresent(byType.order.amount, byType.deposit.amount)
                ? [`Depósitos ${dailyFormat.exactMoney(byType.deposit.amount, currency)} · Órdenes ${dailyFormat.exactMoney(byType.order.amount, currency)}`]
                : []),
            ...(balanceFundedOrders.amount === 0 ? [] : [
                `Excluidas del volumen: órdenes pagadas con saldo ${dailyFormat.exactMoney(balanceFundedOrders.amount, currency)}, ya contadas como depósito`,
            ]),
        ];
    },

    revenueLines: (row: RevenueByCurrencyDto | undefined): string[] =>
        row === undefined || !hasShares(row.byType) || !moneyByType.bothPresent(row.byType.order.amount, row.byType.deposit.amount)
            ? []
            : [`Órdenes ${dailyFormat.exactMoney(row.byType.order.amount, row.currency)} · Depósitos ${dailyFormat.exactMoney(row.byType.deposit.amount, row.currency)}`],
};
